import { Injectable, inject } from '@angular/core';
import { Amendment, isAmendmentInProgress } from '@app/features/amendments/amendment.model';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { HistoryOrigin, HistorySubject } from './history-entry.model';

export const AMENDMENT_ORIGIN: HistoryOrigin = { key: 'avenant', label: 'Avenant' };

/** Le sujet d'une entrée qui porte sur un avenant que le fil ne connaît pas. */
const UNKNOWN_AMENDMENT: HistorySubject = { label: 'Avenant', origin: AMENDMENT_ORIGIN };

const effectiveDate = (amendment: Amendment) =>
  new Date(amendment.effectiveDate).toLocaleDateString('fr-FR', { timeZone: 'UTC' });

/**
 * Le nom d'un avenant dans le fil : son numéro ou, en cours, celui qu'il recevra, comme la liste
 * des contrats ; un avenant abandonné sans numéro se désigne par sa date d'effet.
 */
function label(amendment: Amendment, nextNumber: number): string {
  if (amendment.number !== null) return `Avenant n°${amendment.number}`;
  if (isAmendmentInProgress(amendment)) return `Avenant n°${nextNumber}`;
  return `Avenant sans numéro, effet au ${effectiveDate(amendment)}`;
}

/** Les avenants d'un contrat, et le sujet que chacun donne à ses entrées. */
export interface ContractAmendments {
  readonly amendments: readonly Amendment[];
  /** Le sujet d'une entrée, d'après l'identifiant du sujet qu'elle porte ; aucun pour l'embauche. */
  subjectOf(subjectId: string | null | undefined): HistorySubject | undefined;
}

/**
 * Les avenants du contrat, lus une seule fois pour toutes les sources du fil, qui y rattachent
 * chacune leurs entrées (cadrage 2026-014, décision 06 : l'historique réunit les sujets). Une
 * lecture en échec n'en donne aucun : le reste du fil demeure.
 */
@Injectable()
export class ContractAmendmentsHistory {
  private readonly service = inject(AmendmentService);
  private readonly cache = new Map<string, Observable<ContractAmendments>>();

  of(contract: ContractListItem): Observable<ContractAmendments> {
    let amendments = this.cache.get(contract.id);
    if (!amendments) {
      amendments = this.service.listOfContract(contract.id).pipe(
        catchError(() => of<Amendment[]>([])),
        map((list) => this.subjects(contract, list)),
        shareReplay(1),
      );
      this.cache.set(contract.id, amendments);
    }
    return amendments;
  }

  private subjects(contract: ContractListItem, amendments: Amendment[]): ContractAmendments {
    const nextNumber = Math.max(0, ...amendments.map((a) => a.number ?? 0)) + 1;
    const subjects = new Map<string, HistorySubject>(
      amendments.map((a) => [a.id, { label: label(a, nextNumber), origin: AMENDMENT_ORIGIN }]),
    );
    return {
      amendments,
      subjectOf: (subjectId) =>
        !subjectId || subjectId === contract.id
          ? undefined
          : (subjects.get(subjectId) ?? UNKNOWN_AMENDMENT),
    };
  }
}
